import React, { useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

/**
 * A drop-in replacement for react-router `<Link>` that also scrolls to the
 * hash target element when the user clicks a link whose destination already
 * matches the current URL (e.g. clicking "关于" while already on
 * `/company#about`).  Regular `<Link>` ignores the click in that case because
 * the URL doesn't change, so `useHashScroll` never re-fires.
 */
export const HashLink: React.FC<
  React.ComponentProps<typeof Link> & { className?: string }
> = ({ to, onClick, ...rest }) => {
  const location = useLocation();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.(e);

      const target = typeof to === "string" ? to : to.pathname + (to.hash ?? "");
      if (!target) return;

      // Parse the target into pathname + hash
      const hashIdx = target.indexOf("#");
      const targetPath = hashIdx >= 0 ? target.slice(0, hashIdx) : target;
      const targetHash = hashIdx >= 0 ? target.slice(hashIdx + 1) : "";

      // If we're already on the same page with the same hash, manually scroll
      const currentPath = location.pathname;
      const currentHash = location.hash.replace("#", "");

      if (targetPath === currentPath && targetHash === currentHash && targetHash) {
        e.preventDefault();
        const el = document.getElementById(targetHash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          el.classList.remove("anchor-flash");
          void el.offsetWidth;
          el.classList.add("anchor-flash");
        }
      }
    },
    [to, location.pathname, location.hash, onClick],
  );

  return <Link to={to} onClick={handleClick} {...rest} />;
};
